/**
 * Render functions — static replay-only mode.
 * No chat tab, no gateway status, no scenario select, no run button.
 */

import type { AppState } from "./state.js";
import type { RunInfo } from "./types.js";
import { formatDuration, formatElapsed } from "./utils/format.js";
import {
  iconBaselineLg,
  iconPause,
  iconPlay,
  iconReplay,
  iconStart,
  iconStop,
  iconTreatmentLg,
} from "./utils/icons.js";
import { renderComparison, hideComparison } from "./render/comparison.js";
import { escapeHtml, formatTimestampLabel } from "./render/dom-helpers.js";
import {
  appendEventNode,
  renderTimeline,
  resetPaneUI,
  setReplayAnimationSpeed,
  showTimeline,
  updatePaneTimer,
  upsertEventNode,
} from "./render/timeline.js";

export {
  appendEventNode,
  hideComparison,
  renderComparison,
  renderTimeline,
  resetPaneUI,
  setReplayAnimationSpeed,
  showTimeline,
  updatePaneTimer,
  upsertEventNode,
};

export function renderScaffold(root: HTMLElement) {
  root.innerHTML = `
    <header class="header" id="header">
      <div class="header-brand">
        <span class="header-logo">OmniClaw Benchmark</span>
        <span class="header-badge">REPLAY</span>
      </div>
      <div class="header-controls" id="header-controls">
        <button class="btn-replay" id="btn-replay" title="Select a replay to watch">
          ${iconReplay()} Select Replay
        </button>
        <button class="btn-run btn-run--hidden" id="btn-run" style="display:none">
          ${iconStop()} Stop
        </button>
      </div>
    </header>
    <div class="main-content" id="main-content">
      <div class="pane pane--treatment" id="pane-treatment">
        <div class="pane-header pane-header--treatment">
          <div>
            <div class="pane-title pane-title--treatment">${iconStart()} OmniClaw</div>
            <div class="pane-model">GPT-5.4 + Qwen3 (lite)</div>
          </div>
          <div class="pane-timer" id="timer-treatment">
            <span class="status-dot" id="status-dot-treatment"></span>
            <span id="elapsed-treatment">--:--</span>
          </div>
        </div>
        <div class="pane-body" id="pane-body-treatment">
          <div class="pane-idle" id="idle-treatment">
            <div>
              <div class="pane-idle-icon">
                <div class="idle-ring idle-ring--treatment"></div>
                ${iconTreatmentLg()}
              </div>
              <div class="pane-idle-text">Click <strong>Select Replay</strong> to watch a recorded benchmark run<br/>OmniClaw timeline will appear here</div>
            </div>
          </div>
          <div class="timeline" id="timeline-treatment" style="display:none"></div>
        </div>
      </div>
      <div class="pane pane--baseline" id="pane-baseline">
        <div class="pane-header pane-header--baseline">
          <div>
            <div class="pane-title pane-title--baseline">${iconStart()} OpenClaw</div>
            <div class="pane-model">GPT-5.4 Only</div>
          </div>
          <div class="pane-timer" id="timer-baseline">
            <span class="status-dot" id="status-dot-baseline"></span>
            <span id="elapsed-baseline">--:--</span>
          </div>
        </div>
        <div class="pane-body" id="pane-body-baseline">
          <div class="pane-idle" id="idle-baseline">
            <div>
              <div class="pane-idle-icon">
                <div class="idle-ring idle-ring--baseline"></div>
                ${iconBaselineLg()}
              </div>
              <div class="pane-idle-text">Click <strong>Select Replay</strong> to watch a recorded benchmark run<br/>OpenClaw timeline will appear here</div>
            </div>
          </div>
          <div class="timeline" id="timeline-baseline" style="display:none"></div>
        </div>
      </div>
    </div>
    <div class="comparison comparison-hidden" id="comparison"></div>
    <div class="replay-controls replay-controls--hidden" id="replay-controls"></div>
    <div class="run-picker-overlay" id="run-picker-overlay" style="display:none"></div>
  `;
}

export function updateRunButton(state: AppState) {
  const btn = document.getElementById("btn-run") as HTMLButtonElement;
  if (state.phase === "replaying") {
    btn.style.display = "";
    btn.className = "btn-run btn-run--running";
    btn.disabled = false;
    btn.innerHTML = `${iconStop()} Stop`;
    btn.setAttribute("data-stop-replay", "true");
  } else {
    btn.style.display = "none";
  }
}

export function renderRunPicker(runs: RunInfo[], onSelect: (runId: string) => void) {
  const overlay = document.getElementById("run-picker-overlay")!;
  overlay.style.display = "flex";

  if (runs.length === 0) {
    overlay.innerHTML = `
      <div class="run-picker">
        <div class="run-picker-header">
          <span class="run-picker-title">${iconReplay()} Select a Run to Replay</span>
          <button class="run-picker-close" id="run-picker-close">&times;</button>
        </div>
        <div class="run-picker-empty">
          <p>No replay data available.</p>
        </div>
      </div>
    `;
    document.getElementById("run-picker-close")!.addEventListener("click", () => {
      overlay.style.display = "none";
    });
    return;
  }

  overlay.innerHTML = `
    <div class="run-picker">
      <div class="run-picker-header">
        <span class="run-picker-title">${iconReplay()} Select a Run to Replay</span>
        <button class="run-picker-close" id="run-picker-close">&times;</button>
      </div>
      <div class="run-picker-list">
        ${runs
          .map(
            (run) => `
          <div class="run-picker-item" data-run-id="${escapeHtml(run.id)}">
            <div class="run-picker-item-main">
              <span class="run-picker-item-scenario">${escapeHtml(run.scenario)}</span>
              <span class="run-picker-item-duration">${formatDuration(run.totalDurationMs)}</span>
            </div>
            <div class="run-picker-item-meta">
              <span>${formatTimestampLabel(run.timestamp)}</span>
              <span class="run-picker-item-commit">${escapeHtml(run.commitHash)}</span>
            </div>
          </div>
        `,
          )
          .join("")}
      </div>
    </div>
  `;

  document.getElementById("run-picker-close")!.addEventListener("click", () => {
    overlay.style.display = "none";
  });

  for (const item of Array.from(overlay.querySelectorAll(".run-picker-item"))) {
    item.addEventListener("click", () => {
      const runId = (item as HTMLElement).dataset.runId;
      if (runId) {
        onSelect(runId);
      }
    });
  }
}

export function hideRunPicker() {
  const overlay = document.getElementById("run-picker-overlay");
  if (overlay) {
    overlay.style.display = "none";
  }
}

const SPEED_OPTIONS = [1, 2, 4, 8, 16];

export function renderReplayControls(state: AppState) {
  const el = document.getElementById("replay-controls")!;
  if (!state.replayMode) {
    el.classList.add("replay-controls--hidden");
    el.innerHTML = "";
    return;
  }

  el.classList.remove("replay-controls--hidden");
  const progress = state.replayProgress * 100;
  const scenarioLabel = state.replayMeta?.scenario ?? "Replay";

  el.innerHTML = `
    <div class="replay-controls-inner">
      <div class="replay-badge">REPLAY</div>
      <span class="replay-scenario">${escapeHtml(scenarioLabel)}</span>
      <div class="replay-progress-track">
        <div class="replay-progress-bar" style="width: ${progress}%"></div>
      </div>
      <span class="replay-time">${formatElapsed(state.replayElapsed)} / ${formatElapsed(state.replayTotal)}</span>
      <div class="replay-playback-controls">
        ${
          state.replayPaused
            ? `<button class="replay-btn" data-replay-resume title="Resume">${iconPlay()}</button>`
            : `<button class="replay-btn" data-replay-pause title="Pause">${iconPause()}</button>`
        }
      </div>
      <div class="replay-speed-select">
        ${SPEED_OPTIONS.map(
          (s) =>
            `<button class="replay-speed-btn ${s === state.replaySpeed ? "replay-speed-btn--active" : ""}" data-replay-speed="${s}">${s}x</button>`,
        ).join("")}
      </div>
      <div class="replay-mode-toggle">
        <button class="replay-mode-btn ${state.replayDemoMode ? "replay-mode-btn--active" : ""}" data-replay-demo-toggle="true" title="Smooth uniform playback">Demo</button>
        <button class="replay-mode-btn ${!state.replayDemoMode ? "replay-mode-btn--active" : ""}" data-replay-demo-toggle="false" title="Original timeline playback">Timeline</button>
      </div>
    </div>
  `;
}

export function updateReplayProgress(state: AppState) {
  const bar = document.querySelector(".replay-progress-bar") as HTMLElement | null;
  if (bar) {
    bar.style.width = `${state.replayProgress * 100}%`;
  }
  const timeEl = document.querySelector(".replay-time") as HTMLElement | null;
  if (timeEl) {
    timeEl.textContent = `${formatElapsed(state.replayElapsed)} / ${formatElapsed(state.replayTotal)}`;
  }
  const controls = document.querySelector(".replay-playback-controls") as HTMLElement | null;
  if (controls) {
    controls.innerHTML = state.replayPaused
      ? `<button class="replay-btn" data-replay-resume title="Resume">${iconPlay()}</button>`
      : `<button class="replay-btn" data-replay-pause title="Pause">${iconPause()}</button>`;
  }
}
